import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Briefcase, Loader2, MapPin, Calendar as CalendarIcon, Clock, DollarSign,
  Trash2, Users, MessageSquare, CheckCircle2, XCircle, RefreshCw, ExternalLink,
} from "lucide-react";
import { PageHeader } from "@/business/components/PageHeader";
import {
  listMyHireRequests, getHireRequestApplications, acceptApplication,
  rejectApplication, deleteHireRequest,
} from "@/business/api";

function formatBudget(g) {
  if (g.budget === null || g.budget === undefined || g.budget === "") return null;
  return `${(g.currency || "").trim()} ${Number(g.budget).toLocaleString()}`.trim();
}
function statusBadge(status) {
  const s = String(status || "").toLowerCase();
  const classes = s === "open"
    ? "bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
    : s === "closed"
      ? "bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-100"
      : "bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100";
  return <Badge className={`border ${classes}`} data-testid="gig-status-badge">{s || "—"}</Badge>;
}
function perfLabel(v) {
  const m = { singer: "Singer", beatboxer: "Beatboxer", dj: "DJ", jazz: "Jazz", band: "Band", other: "Other" };
  return m[String(v || "").toLowerCase()] || (v || "Performer");
}

export default function Gigs() {
  const navigate = useNavigate();
  const [gigs, setGigs] = useState(null);
  const [error, setError] = useState(null);
  const [activeGig, setActiveGig] = useState(null);
  const [apps, setApps] = useState(null);
  const [appsLoading, setAppsLoading] = useState(false);
  const [acting, setActing] = useState({}); // applicationId -> "accept" | "reject"
  const [deleting, setDeleting] = useState({}); // requestId -> true

  const load = async () => {
    setGigs(null);
    setError(null);
    try {
      const list = await listMyHireRequests();
      setGigs(Array.isArray(list) ? list : []);
    } catch (err) {
      if (err.status === 401 || err.status === 403) { navigate("/business/login", { replace: true }); return; }
      setError(err);
      setGigs([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openGig = async (g) => {
    setActiveGig(g);
    setApps(null);
    setAppsLoading(true);
    try {
      const list = await getHireRequestApplications(g.id);
      setApps(Array.isArray(list) ? list : []);
    } catch (err) {
      toast.error(err.message || "Could not load applications.");
      setApps([]);
    } finally { setAppsLoading(false); }
  };

  const onDecide = async (app, decision) => {
    setActing((s) => ({ ...s, [app.id]: decision }));
    try {
      if (decision === "accept") await acceptApplication(app.id);
      else await rejectApplication(app.id);
      toast.success(decision === "accept" ? "Application accepted." : "Application rejected.");
      setApps((arr) => (arr || []).map((a) => a.id === app.id ? { ...a, status: decision === "accept" ? "accepted" : "rejected" } : a));
    } catch (err) {
      toast.error(err.message || `Could not ${decision} application.`);
    } finally { setActing((s) => { const n = { ...s }; delete n[app.id]; return n; }); }
  };

  const onDelete = async (g) => {
    setDeleting((s) => ({ ...s, [g.id]: true }));
    try {
      await deleteHireRequest(g.id);
      toast.success("Gig deleted.");
      setGigs((arr) => (arr || []).filter((x) => x.id !== g.id));
      if (activeGig && activeGig.id === g.id) { setActiveGig(null); setApps(null); }
    } catch (err) {
      toast.error(err.message || "Could not delete gig.");
    } finally { setDeleting((s) => { const n = { ...s }; delete n[g.id]; return n; }); }
  };

  return (
    <div className="space-y-6" data-testid="my-gigs-page">
      <PageHeader
        title="My Posted Gigs"
        description="Track applications and manage the gigs you've posted."
        action={(
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={load} disabled={gigs === null} data-testid="my-gigs-refresh-button"><RefreshCw className={`mr-2 h-4 w-4 ${gigs === null ? "animate-spin" : ""}`} />Refresh</Button>
            <Button onClick={() => navigate("/business/dashboard/hire")} className="bg-[#FF6A00] hover:bg-[#E85F00] text-white" data-testid="my-gigs-post-button">Post a Gig</Button>
          </div>
        )}
      />

      {gigs === null && (
        <div className="flex items-center justify-center py-16" data-testid="my-gigs-loading"><Loader2 className="h-6 w-6 animate-spin text-[#FF6A00]" /></div>
      )}

      {gigs !== null && error && (
        <Card className="border-red-200 bg-red-50/40" data-testid="my-gigs-error">
          <CardContent className="p-5 text-sm text-[#B91C1C]">Couldn’t load your gigs: {error.message}</CardContent>
        </Card>
      )}

      {gigs !== null && !error && gigs.length === 0 && (
        <Card className="border-[#E5E7EB] shadow-sm" data-testid="my-gigs-empty-state">
          <CardContent className="p-10 text-center">
            <div className="mx-auto h-12 w-12 rounded-2xl bg-[#FFF1E8] text-[#FF6A00] flex items-center justify-center"><Briefcase className="h-6 w-6" /></div>
            <h2 className="mt-3 text-base font-semibold">No gigs posted yet</h2>
            <p className="text-sm text-[#6B7280] mt-1">Post your first gig to start receiving applications from buskers.</p>
            <Button onClick={() => navigate("/business/dashboard/hire")} className="mt-4 bg-[#FF6A00] hover:bg-[#E85F00] text-white" data-testid="my-gigs-empty-cta">Post a Gig</Button>
          </CardContent>
        </Card>
      )}

      {gigs && gigs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {gigs.map((g) => {
            const budget = formatBudget(g);
            const applicationCount = g.application_count ?? 0;
            return (
              <Card key={g.id} className="border-[#E5E7EB] shadow-sm hover:shadow-md transition-shadow" data-testid={`my-gigs-card-${g.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold truncate" data-testid={`my-gigs-card-title-${g.id}`}>{g.event_type}</h3>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        {statusBadge(g.status)}
                        <Badge variant="outline" className="border-[#E5E7EB] text-[#6B7280]">{perfLabel(g.performer_type)}</Badge>
                      </div>
                    </div>
                  </div>
                  <ul className="mt-3 space-y-1.5 text-sm text-[#374151]">
                    <li className="flex items-start gap-2"><MapPin className="h-4 w-4 text-[#6B7280] flex-shrink-0 mt-0.5" /><span className="truncate">{g.location || "—"}</span></li>
                    <li className="flex items-start gap-2"><CalendarIcon className="h-4 w-4 text-[#6B7280] flex-shrink-0 mt-0.5" /><span>{g.event_date_display || g.event_date || "Date TBC"}</span></li>
                    {g.event_time && <li className="flex items-start gap-2"><Clock className="h-4 w-4 text-[#6B7280] flex-shrink-0 mt-0.5" /><span>{g.event_time} · {g.duration || ""}</span></li>}
                    {budget && <li className="flex items-start gap-2"><DollarSign className="h-4 w-4 text-[#6B7280] flex-shrink-0 mt-0.5" /><span className="tabular-nums">{budget}</span></li>}
                  </ul>
                  <div className="mt-4 flex items-center justify-between gap-2">
                    <Button variant="outline" size="sm" onClick={() => openGig(g)} data-testid={`my-gigs-view-applications-${g.id}`}>
                      <Users className="mr-2 h-4 w-4" />{applicationCount} application{applicationCount === 1 ? "" : "s"}
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-[#DC2626] hover:text-[#B91C1C] hover:bg-red-50" data-testid={`my-gigs-delete-button-${g.id}`} disabled={!!deleting[g.id]}>
                          {deleting[g.id] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this gig?</AlertDialogTitle>
                          <AlertDialogDescription>The gig will be removed from the busker app immediately. This can’t be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => onDelete(g)} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid={`my-gigs-delete-confirm-${g.id}`}>Yes, delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Sheet open={!!activeGig} onOpenChange={(o) => { if (!o) { setActiveGig(null); setApps(null); } }}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="my-gigs-applications-sheet">
          <SheetHeader>
            <SheetTitle className="truncate">{activeGig?.event_type || "Gig"}</SheetTitle>
            <SheetDescription>{activeGig?.location} · {activeGig?.event_date_display || activeGig?.event_date}</SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            <h3 className="text-sm font-semibold mb-2">Applications</h3>
            {appsLoading && (
              <div className="py-8 flex justify-center" data-testid="my-gigs-applications-loading"><Loader2 className="h-5 w-5 animate-spin text-[#FF6A00]" /></div>
            )}
            {!appsLoading && apps && apps.length === 0 && (
              <div className="py-8 text-center" data-testid="my-gigs-applications-empty">
                <div className="mx-auto h-10 w-10 rounded-2xl bg-[#F3F4F6] text-[#6B7280] flex items-center justify-center"><Users className="h-5 w-5" /></div>
                <p className="mt-2 text-sm font-medium">No applications yet</p>
                <p className="text-xs text-[#6B7280]">Buskers in the area can see your gig in the BuskerBeat mobile app.</p>
              </div>
            )}
            {!appsLoading && apps && apps.length > 0 && (
              <ul className="space-y-3" data-testid="my-gigs-applications-list">
                {apps.map((a) => {
                  const aStatus = String(a.status || "pending").toLowerCase();
                  return (
                    <li key={a.id} className="border border-[#E5E7EB] rounded-lg p-3" data-testid={`my-gigs-application-row-${a.id}`}>
                      <div className="flex items-start gap-3">
                        {a.busker_photo ? (
                          <img src={a.busker_photo} alt="" className="h-10 w-10 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <span className="h-10 w-10 rounded-full bg-[#FFF1E8] text-[#FF6A00] flex items-center justify-center text-sm font-semibold flex-shrink-0">{(a.busker_name || "B").slice(0,1).toUpperCase()}</span>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-sm font-semibold truncate">{a.busker_name || "Busker"}</div>
                            <Badge className={
                              aStatus === "accepted" ? "bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100 border" :
                              aStatus === "rejected" ? "bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-100 border" :
                              "bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100 border"
                            }>{aStatus}</Badge>
                          </div>
                          {a.busker_username && (
                            <a href={`/busker/${a.busker_username}`} target="_blank" rel="noreferrer" className="text-xs text-[#FF6A00] hover:underline inline-flex items-center gap-1" data-testid={`my-gigs-application-profile-link-${a.id}`}>View profile <ExternalLink className="h-3 w-3" /></a>
                          )}
                          {a.cover_letter && (
                            <div className="mt-2 flex items-start gap-2 text-sm text-[#374151] bg-[#F9FAFB] border border-[#E5E7EB] rounded-md p-2"><MessageSquare className="h-4 w-4 text-[#6B7280] flex-shrink-0 mt-0.5" /><span className="whitespace-pre-line break-words">{a.cover_letter}</span></div>
                          )}
                          {aStatus === "pending" && (
                            <div className="mt-3 flex items-center gap-2">
                              <Button size="sm" onClick={() => onDecide(a, "accept")} disabled={!!acting[a.id]} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`my-gigs-application-accept-${a.id}`}>
                                {acting[a.id] === "accept" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}Accept
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => onDecide(a, "reject")} disabled={!!acting[a.id]} data-testid={`my-gigs-application-reject-${a.id}`}>
                                {acting[a.id] === "reject" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <XCircle className="mr-2 h-4 w-4" />}Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
